import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import TableDrag from './TableDrag';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface PhaseItem {
  content: string;
  edit: boolean;
}

interface TableMainProps {
  items: PhaseItem[];
  saveNewSet: any;
  editPhases: (id: number) => void;
  onKeyDownEdit: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleChangeEdit: (e: React.ChangeEvent<HTMLInputElement>) => void;
  selectValue: string;
  saveEditPhase: (id: number) => void;
  clickDelete: (id: number) => void;
}

// ─── Styled components ─────────────────────────────────────────────────────────

const MyTableWrapper = styled.div`
  .fixed_header {
    width: 350px;
    table-layout: fixed;
    border-collapse: collapse;
    border: 1px solid #ddd;
    margin-bottom: 15px !important;

    & > tbody {
      display: block;
      width: 350px;
      overflow-y: auto;
      overflow-x: hidden;
      height: 200px;
      cursor: grabbing;
      background: #fdfdfd;
    }

    & > thead {
      background: yellow;
      color: black;

      & > tr {
        display: block;
      }
    }

    & > thead th,
    & > tbody td {
      padding: 5px;
      text-align: left;
      border: 0;
      padding-bottom: 0;
    }

    & > tbody tr:nth-child(odd) td {
      background-color: #f1f1f1;
    }

    & > tbody tr:nth-child(even) td {
      background-color: #fdfdfd;
    }
  }
`;

// ─── Component ─────────────────────────────────────────────────────────────────

const TableMain: React.FC<TableMainProps> = ({
  items: itemsProp,
  saveNewSet,
  editPhases,
  onKeyDownEdit,
  handleChangeEdit,
  selectValue,
  saveEditPhase,
  clickDelete,
}) =>
{
  const [items, setItems] = useState<PhaseItem[]>(itemsProp);

  // Sync internal state when parent updates the list
  useEffect(() =>
  {
    setItems(itemsProp);
    saveNewSet(itemsProp);
  }, [itemsProp]);

  const sensors = useSensors(useSensor(PointerSensor));

  // dnd-kit identifies items by id, so we derive stable string ids from the index
  const itemIds = items.map((_, i) => String(i));

  const onDragEnd = useCallback(
    ({ active, over }: DragEndEvent) =>
    {
      if (!over || active.id === over.id) return;
      const oldIndex = itemIds.indexOf(String(active.id));
      const newIndex = itemIds.indexOf(String(over.id));
      setItems(prev => arrayMove(prev, oldIndex, newIndex));
      saveNewSet((prev: PhaseItem[]) => arrayMove(prev, oldIndex, newIndex));
    },
    [itemIds],
  );

  return (
    <MyTableWrapper>
      <table className="table table-dark fixed_header">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          onDragEnd={onDragEnd}
        >
          <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
            <tbody>
              {items.map((item, index) => (
                <TableDrag
                  key={`item-${index}`}
                  id={String(index)}
                  content={item.content}
                  edit={item.edit}
                  editPhases={() => editPhases(index)}
                  onKeyDownEdit={onKeyDownEdit}
                  handleChangeEdit={handleChangeEdit}
                  selectValue={selectValue}
                  saveEditPhase={() => saveEditPhase(index)}
                  clickDelete={() => clickDelete(index)}
                />
              ))}
            </tbody>
          </SortableContext>
        </DndContext>
      </table>
    </MyTableWrapper>
  );
};

export default TableMain;
